<script setup>
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAcesso } from 'dashboard/composables/useAcesso';
import AcessoAPI from 'dashboard/api/acesso';
import {
  SEM_ACESSO,
  escoposDoModulo,
  gruposDoModulo,
  naoSeDa,
} from '../catalogo';
import { fraseDoErro } from '../fraseDoErro';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import CaixaDeEscopo from './CaixaDeEscopo.vue';

// ACESSO-01 (L9a): criar e editar um grupo da conta, na aba Grupos e por cima do editor do papel ("Criar grupo", com o
// módulo da linha fixo). O grupo novo escolhe o módulo e pode partir de um grupo da Powerbizz, que preenche os escopos; o
// grupo que existe não troca de módulo. O `salvo` leva o grupo como a API o devolve. Só os escopos que se concedem entram: o servidor
// recusa os outros. O que falta a quem edita fica desabilitado, com a nota (R6), e o "a partir de" não o marca.
const emit = defineEmits(['salvo']);

const { t } = useI18n();
const { pode } = useAcesso();
const catalogo = useMapGetter('acesso/catalogo');
const dialogo = useTemplateRef('dialogo');

// O grupo da conta que se edita, ou nenhum no grupo novo.
const editando = ref(null);
const modulo = ref('');
// O grupo que existe e o que nasce no editor do papel não escolhem o módulo.
const moduloFixo = ref(false);
const aPartirDe = ref('');
const nome = ref('');
const marcados = ref([]);
const salvando = ref(false);
const erro = ref('');

const modulosAbertos = computed(() =>
  (catalogo.value?.modulos || []).filter(cada => !cada.travado)
);
const opcoesDeModulo = computed(() =>
  modulosAbertos.value.map(cada => ({ value: cada.chave, label: cada.rotulo }))
);
const escopos = computed(() =>
  escoposDoModulo(catalogo.value, modulo.value).filter(
    escopo => escopo.concedivel
  )
);
const daPowerbizz = computed(() =>
  gruposDoModulo(catalogo.value, modulo.value).filter(
    grupo =>
      grupo.origem === 'powerbizz' &&
      grupo.chave !== SEM_ACESSO &&
      !grupo.so_modelo
  )
);
const opcoesDePartida = computed(() => [
  { value: '', label: t('PAPEIS.GRUPOS.DIALOGO.EM_BRANCO') },
  ...daPowerbizz.value.map(grupo => ({
    value: grupo.chave,
    label: grupo.rotulo,
  })),
]);

const podeSalvar = computed(
  () =>
    Boolean(modulo.value) &&
    Boolean(nome.value.trim()) &&
    marcados.value.length > 0
);

const escolherModulo = chave => {
  modulo.value = chave;
  aPartirDe.value = '';
  marcados.value = [];
};

const bloqueado = escopo => naoSeDa(escopo, pode);

const partirDe = chave => {
  aPartirDe.value = chave;
  const grupo = daPowerbizz.value.find(cada => cada.chave === chave);
  const seDao = new Set(
    escopos.value
      .filter(escopo => !bloqueado(escopo))
      .map(escopo => escopo.nome)
  );
  marcados.value = grupo
    ? grupo.escopos.filter(escopo => seDao.has(escopo))
    : [];
};

const marcar = (escopo, marcado) => {
  marcados.value = marcado
    ? [...marcados.value, escopo]
    : marcados.value.filter(cada => cada !== escopo);
};

// `modulo` vem do editor do papel, que o fixa. O módulo travado ou fora do catálogo fica sem escolha.
const abrir = ({ grupo = null, modulo: doEditor = '', fixo = false } = {}) => {
  editando.value = grupo;
  const doCatalogo = modulosAbertos.value.some(cada => cada.chave === doEditor);
  modulo.value = grupo?.modulo || (doCatalogo ? doEditor : '');
  moduloFixo.value = Boolean(grupo) || (fixo && doCatalogo);
  aPartirDe.value = '';
  nome.value = grupo?.nome || '';
  marcados.value = [...(grupo?.escopos || [])];
  erro.value = '';
  dialogo.value?.open();
};

const salvar = async () => {
  if (!podeSalvar.value) return;
  salvando.value = true;
  erro.value = '';
  const corpo = {
    nome: nome.value.trim(),
    escopos: escopos.value
      .map(escopo => escopo.nome)
      .filter(escopo => marcados.value.includes(escopo)),
  };
  try {
    let resposta;
    if (editando.value) {
      resposta = await AcessoAPI.atualizarGrupo(editando.value.id, corpo);
      useAlert(t('PAPEIS.GRUPOS.DIALOGO.SALVO'));
    } else {
      resposta = await AcessoAPI.criarGrupo({ modulo: modulo.value, ...corpo });
      useAlert(t('PAPEIS.GRUPOS.DIALOGO.CRIADO'));
    }
    dialogo.value?.close();
    emit('salvo', resposta.data);
  } catch (falha) {
    erro.value = fraseDoErro(falha, t('PAPEIS.GRUPOS.DIALOGO.ERRO_AO_SALVAR'));
  } finally {
    salvando.value = false;
  }
};

defineExpose({ abrir });
</script>

<template>
  <Dialog
    ref="dialogo"
    width="xl"
    :title="
      editando ? t('PAPEIS.GRUPOS.DIALOGO.EDITAR') : t('PAPEIS.GRUPOS.NOVO')
    "
    :description="t('PAPEIS.GRUPOS.DIALOGO.FRASE')"
    :confirm-button-label="
      editando
        ? t('PAPEIS.GRUPOS.DIALOGO.SALVAR')
        : t('PAPEIS.GRUPOS.DIALOGO.CRIAR')
    "
    :disable-confirm-button="!podeSalvar"
    :is-loading="salvando"
    @confirm="salvar"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap gap-4">
        <label class="flex flex-col gap-1">
          {{ t('PAPEIS.GRUPOS.DIALOGO.MODULO') }}
          <Select
            :model-value="modulo"
            :options="opcoesDeModulo"
            :placeholder="t('PAPEIS.GRUPOS.DIALOGO.MODULO_ESCOLHA')"
            :disabled="moduloFixo"
            size="sm"
            data-modulo-do-grupo
            @update:model-value="escolherModulo"
          />
        </label>
        <label v-if="!editando" class="flex flex-col gap-1">
          {{ t('PAPEIS.GRUPOS.DIALOGO.A_PARTIR_DE') }}
          <Select
            :model-value="aPartirDe"
            :options="opcoesDePartida"
            :disabled="!modulo"
            size="sm"
            data-a-partir-de
            @update:model-value="partirDe"
          />
        </label>
      </div>
      <Input
        v-model="nome"
        :label="t('PAPEIS.GRUPOS.DIALOGO.NOME')"
        :placeholder="t('PAPEIS.GRUPOS.DIALOGO.NOME_EXEMPLO')"
        custom-input-class="placeholder:!text-n-slate-11 dark:placeholder:!text-n-slate-11"
        data-nome-do-grupo
      />
      <fieldset v-if="modulo" class="flex flex-col p-0 m-0 border-0">
        <legend class="p-0 mb-1 text-heading-3 text-n-slate-12">
          {{ t('PAPEIS.GRUPOS.DIALOGO.ESCOPOS') }}
        </legend>
        <CaixaDeEscopo
          v-for="escopo in escopos"
          :key="escopo.nome"
          :escopo="escopo"
          :model-value="marcados.includes(escopo.nome)"
          :disabled="bloqueado(escopo)"
          :nota="bloqueado(escopo) ? t('PAPEIS.NAO_CONCEDIVEL') : ''"
          @update:model-value="marcado => marcar(escopo.nome, marcado)"
        />
      </fieldset>
      <p
        v-if="erro"
        role="alert"
        class="flex items-start gap-2 mb-0 text-sm text-n-slate-12"
        data-erro-do-grupo
      >
        <span
          class="i-lucide-circle-alert size-4 mt-0.5 shrink-0 text-n-ruby-11"
          aria-hidden="true"
        />
        {{ erro }}
      </p>
    </div>
  </Dialog>
</template>
